import React from 'react'

import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'

import { useQueryPaginator } from 'src/components/Table'
import { FacilityDetailTable } from 'src/components/Table/detail/Facility'
import { FacilityDialog } from 'src/modules/supply_chain/components/FacilityDialog'
import { ASNListTable } from 'src/components/Table/list/ASN'
import { PalletListTable } from 'src/components/Table/list/Pallet'
import { DamagedListTable } from 'src/components/Table/list/Damaged'
import { FreightOrderListTable } from 'src/components/Table/list/FreightOrder'
import { FreightShipmentListTable } from 'src/components/Table/list/FreightShipment'
import { ParcelOrderListTable } from 'src/components/Table/list/ParcelOrder'
import { ParcelShipmentListTable } from 'src/components/Table/list/ParcelShipment'
import { SkuListTable } from 'src/components/Table/list/Sku'

import { skuit } from 'src/services/skuit'
import { useQuery } from 'react-query'

import { DetailPage } from 'src/components/Pages/DetailPage'
import { DetailTab } from 'src/components/Pages/Tab'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

interface PathProps {
  guid: string
}

const InventoryTab = () => {
  const { guid } = useParams<PathProps>()

  const skusPaginator = useQueryPaginator('sku-list', (config) => skuit.client.facilities.skus.list(guid, config), {
    params: { page_size: 5, facilities: guid, storing: true },
  })
  const palletPaginator = useQueryPaginator('container-list', skuit.client.pallets.list, {
    params: { page_size: 5, facilities: guid },
  })
  const damagedPaginator = useQueryPaginator('damaged-list', skuit.client.damaged.list, {
    params: { page_size: 5, facilities: guid },
  })

  return (
    <DetailTab>
      <SkuListTable
        paginator={skusPaginator}
        title="SKUs in Facility"
        fields={['sku', 'productName', 'numAvailable', 'numReserved', 'numAwaitingShipment', 'isActive']}
      />
      <PalletListTable
        paginator={palletPaginator}
        title="Pallets in Facility"
        fields={['name', 'guid', 'skus']}
      />
      <DamagedListTable
        paginator={damagedPaginator}
        title="Damages in Facility"
        fields={['guid', 'product', 'sku']}
      />
    </DetailTab>
  )
}

const InboundTab = () => {
  const { guid } = useParams<PathProps>()

  const asnPaginator = useQueryPaginator('asn-list', skuit.client.asn.list, {
    params: { page_size: 5, facilities: guid },
  })

  return (
    <DetailTab>
      <ASNListTable
        paginator={asnPaginator}
        fields={["trackingNumber", "consignee", "carrier", "status"]}
      />
    </DetailTab>
  )
}

const FreightTab = () => {
  const { guid } = useParams<PathProps>()

  const ordersPaginator = useQueryPaginator('freight-order-list', skuit.client.freight.orders.list, {
    params: { page_size: 5, facilities: guid },
  })
  const shipmentsPaginator = useQueryPaginator('freight-shipment-list', skuit.client.freight.shipments.list, {
    params: { page_size: 5, facilities: guid },
  })

  return (
    <DetailTab>
      <FreightOrderListTable
        paginator={ordersPaginator}
        fields={["number", "placedOn", "fulfillOn", "status"]}
      />
      <FreightShipmentListTable
        paginator={shipmentsPaginator}
        fields={["trackingNumber", "consignee", "status", "order", "shippedOn"]}
      />
    </DetailTab>
  )
}

const ParcelTab = () => {
  const { guid } = useParams<PathProps>()

  const ordersPaginator = useQueryPaginator('parcel-order-list', skuit.client.parcel.orders.list, {
    params: { page_size: 5, facilities: guid },
  })
  const shipmentsPaginator = useQueryPaginator('parcel-shipment-list', skuit.client.parcel.shipments.list, {
    params: { page_size: 5, facilities: guid },
  })

  return (
    <DetailTab>
      <ParcelOrderListTable
        paginator={ordersPaginator}
        fields={["number", "placedOn", "fulfillOn", "status"]}
      />
      <ParcelShipmentListTable
        paginator={shipmentsPaginator}
        fields={["trackingNumber", "consignee", "status", "order", "shippedOn"]}
      />
    </DetailTab>
  )
}

export const FacilityPage = () => {
  const { guid } = useParams<PathProps>()

  const facility = useQuery(['facility-details', guid], () => skuit.client.facilities.retrieve(guid))


  const refresh = () => {
    facility.refetch()
  }

  return (
    <>
      <DetailPage
        obj={facility}
        title="Facilities"
        refresh={refresh}
        breadcrumbs={[{ title: 'Dashboard', url: '/' }, { title: 'Facilities', url: '/warehouse/facilities' }]}
        detailComponent={<FacilityDetailTable obj={facility} refresh={refresh} />}
        createDialog={{ title: 'New Facility', Dialog: FacilityDialog }}
        tabs={['Inventory', 'Inbound', 'Freight', 'Parcel']}
      >
        <InventoryTab />
        <InboundTab />
        <FreightTab />
        <ParcelTab />
      </DetailPage>
    </>
  )
}
